import { useId, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, MouseEvent } from 'react';
import type { Breed } from '../breeds';
import { filterBreeds, findExactBreed } from '../breedSearch';
import styles from '../Fetch.module.css';

interface FetchBreedComboboxProps {
  breeds: Breed[];
  /** Selected breed id; '' while nothing has been picked. */
  value: string;
  onChange: (breedId: string) => void;
  label?: string;
}

const EMPTY_PLACEHOLDER = 'Start typing a breed';

// Passed as the ref of the highlighted option only, so React calls it each time
// the highlight lands on a new option -- from the arrow keys, from typing, or
// from opening on the picked breed -- and not on re-renders that leave the
// highlight where it was. Defined outside the component so it keeps the same
// identity between renders; a new function each render would scroll every time.
const scrollOptionIntoView = (node: HTMLLIElement | null) => {
  // jsdom has no scrollIntoView
  node?.scrollIntoView?.({ block: 'nearest' });
};

// Replaces a 400-option native <select> with a type-to-filter list, matching
// the pattern Fetch uses on its own quote flow. The value contract is
// unchanged -- a breed id, or '' for "not picked" -- so usePetForm's submit
// gate still refuses a half-typed breed.
export function FetchBreedCombobox({
  breeds,
  value,
  onChange,
  label = 'What breed?',
}: FetchBreedComboboxProps) {
  const id = useId();
  const inputId = `${id}-input`;
  const listId = `${id}-list`;
  const optionId = (index: number) => `${id}-option-${index}`;

  const inputRef = useRef<HTMLInputElement>(null);

  const [open, setOpen] = useState(false);
  // null while closed, so the field shows the picked breed. Opening sets it to
  // '' so typing starts fresh instead of appending to "Afghan Hound"; the
  // picked name sits in the placeholder meanwhile, so it is not lost from view.
  const [query, setQuery] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(-1);

  const selected = useMemo(
    () => breeds.find((b) => b.id === value),
    [breeds, value],
  );
  const options = useMemo(
    () => filterBreeds(breeds, query ?? ''),
    [breeds, query],
  );

  const openList = () => {
    if (open) return;
    setOpen(true);
    setQuery('');
    // Unfiltered list on open, so the picked breed's index is its list index.
    setActiveIndex(selected ? breeds.indexOf(selected) : -1);
  };

  const closeList = () => {
    setOpen(false);
    setQuery(null);
    setActiveIndex(-1);
  };

  const commit = (breed: Breed) => {
    if (breed.id !== value) onChange(breed.id);
    closeList();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!open) {
          openList();
          if (!selected) setActiveIndex(0);
          return;
        }
        if (options.length > 0) {
          setActiveIndex((i) => Math.min(i + 1, options.length - 1));
        }
        return;
      case 'ArrowUp':
        e.preventDefault();
        if (open && options.length > 0) {
          setActiveIndex((i) => Math.max(i - 1, 0));
        }
        return;
      case 'Enter':
        // With the list open, Enter picks -- it must never submit the form on
        // a half-typed breed. With it closed, Enter submits as any field does.
        if (!open) return;
        e.preventDefault();
        if (options[activeIndex]) commit(options[activeIndex]);
        return;
      case 'Escape':
        // First Escape closes the list, not the Fetch modal behind it.
        if (!open) return;
        e.preventDefault();
        e.stopPropagation();
        closeList();
        return;
      default:
    }
  };

  const handleBlur = () => {
    if (query) {
      const exact = findExactBreed(breeds, query);
      if (exact && exact.id !== value) onChange(exact.id);
    }
    closeList();
  };

  // The old <select> sat inside a <label>, so the whole box was clickable.
  // Keep that for the box's padding without letting the list's clicks through.
  const handleBoxMouseDown = (e: MouseEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    e.preventDefault();
    inputRef.current?.focus();
  };

  const trimmedQuery = query?.trim() ?? '';
  const placeholder = open && selected ? selected.name : EMPTY_PLACEHOLDER;

  return (
    <div
      className={`${styles.fieldBox} ${styles.comboBox}`}
      onMouseDown={handleBoxMouseDown}
    >
      <label htmlFor={inputId} className={styles.fieldLabel}>
        {label}
      </label>
      <input
        ref={inputRef}
        id={inputId}
        className={`${styles.fieldInput} ${styles.comboInput}`}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={
          open && activeIndex >= 0 ? optionId(activeIndex) : undefined
        }
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        value={query ?? selected?.name ?? ''}
        placeholder={placeholder}
        onFocus={openList}
        onClick={openList}
        onChange={(e) => {
          const raw = e.target.value;
          // With the list closed but focus kept (after a pick or Escape) the
          // field shows the picked name, so what the shopper types lands on
          // the end of it. That is a new search, not an edit of the name.
          const next =
            query === null && selected && raw.startsWith(selected.name)
              ? raw.slice(selected.name.length)
              : raw;
          setOpen(true);
          setQuery(next);
          setActiveIndex(
            next.trim() && filterBreeds(breeds, next).length > 0 ? 0 : -1,
          );
        }}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
      />

      {open && (
        <div
          className={styles.comboPopup}
          // Keeps focus in the input through option clicks and scrollbar drags.
          onMouseDown={(e) => e.preventDefault()}
        >
          <ul
            id={listId}
            role="listbox"
            aria-label={label}
            className={styles.comboList}
          >
            {options.map((breed, index) => (
              <li
                key={breed.id}
                ref={index === activeIndex ? scrollOptionIntoView : undefined}
                id={optionId(index)}
                role="option"
                aria-selected={breed.id === value}
                className={[
                  styles.comboOption,
                  index === activeIndex ? styles.comboOptionActive : '',
                  breed.id === value ? styles.comboOptionSelected : '',
                ].join(' ')}
                onMouseMove={() => {
                  if (index !== activeIndex) setActiveIndex(index);
                }}
                onClick={() => commit(breed)}
              >
                {breed.name}
              </li>
            ))}
          </ul>
          {options.length === 0 && (
            <p className={styles.comboEmpty} role="status">
              No breeds match &ldquo;{trimmedQuery}&rdquo;. If you&apos;re not
              sure, choose a Mixed Breed size.
            </p>
          )}
        </div>
      )}

      <span className={styles.srOnly} aria-live="polite">
        {open && trimmedQuery && options.length > 0
          ? `${options.length} ${options.length === 1 ? 'breed' : 'breeds'} found`
          : ''}
      </span>
    </div>
  );
}
